import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Input,
  Select,
  Tabs,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import { api } from '@/lib/api';
import { useUrlState } from '@/lib/url-state';
import { PageContainer } from '@/layouts/PageContainer';
import { whenLabel } from '@/pages/calendar/time';
import { OperationalRecords } from '@/pages/ops/OperationalRecords';
import styles from '@/pages/ops/parity.module.css';
import { FormDialog } from './FormDialog';

type Tab = 'notif' | 'obs' | 'adr' | 'emerg' | 'incident' | 'safe';
interface PatientRef {
  id: string;
  fullName: string;
}
interface Observation {
  recordId: string;
  patient: PatientRef;
  vaccine: string;
  dose: string | null;
  givenAt: string;
  observeMinutes: number;
  watchUntil: string;
  status: 'watching' | 'due_check' | 'cleared' | 'reaction';
}
interface Reaction {
  recordId: string;
  patient: PatientRef;
  medicine: string;
  reaction: string;
  severity: string | null;
  recordedAt: string;
  recordedBy: string;
  npra: { reference: string | null; recordedAt: string } | null;
}
interface Emergency {
  recordId: string;
  patient: PatientRef;
  step: string;
  note: string | null;
  recordedAt: string;
  recordedBy: string;
}
interface Safeguarding {
  recordId: string;
  kind: 'child_protection' | 'domestic_violence';
  patient: PatientRef;
  recordedAt: string;
  recordedBy: string;
  summary: Record<string, unknown>;
}

function useSafety<T>(path: string, enabled = true) {
  const { selected } = useBranch();
  return useQuery({
    queryKey: ['safety', path, selected?.id ?? 'all'],
    enabled,
    queryFn: ({ signal }) =>
      api.get<T>(`/safety/${path}`, { signal }).then((r) => r.data),
  });
}
function useSafetyAction<V>(run: (v: V) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['safety'] }),
  });
}

const patient = (p: PatientRef) => (
  <Link to={`/patients/${p.id}`}>{p.fullName}</Link>
);

/**
 * Safety & public health (Admin v2 hub): notifiable diseases, observation
 * after vaccination, drug reactions (NPRA), emergencies, incidents and the
 * restricted safeguarding list. Lists come from consultation records and
 * the registers; nothing is sent to an authority from here.
 */
export function SafetyPage() {
  const { hasPermission } = useSession();
  const canManage = hasPermission('safety:manage');
  const canSafeguard = hasPermission('safeguarding:read');
  // RL-1857: the incident register needs compliance:read (doctors lack it).
  const canIncidents = hasPermission('compliance:read');
  const [tab, setTab] = useUrlState<Tab>('tab', 'notif', [
    'notif',
    'obs',
    'adr',
    'emerg',
    ...(canIncidents ? (['incident'] as const) : []),
    'safe',
  ]);
  const [open, setOpen] = useState<
    { kind: 'check'; o: Observation } | { kind: 'npra'; r: Reaction } | null
  >(null);
  const items: { value: Tab; label: string }[] = [
    { value: 'notif', label: 'Notifiable diseases' },
    { value: 'obs', label: 'After vaccination' },
    { value: 'adr', label: 'Drug reactions' },
    { value: 'emerg', label: 'Emergencies' },
    ...(canIncidents ? [{ value: 'incident' as Tab, label: 'Incidents' }] : []),
    ...(canSafeguard ? [{ value: 'safe' as Tab, label: 'Safeguarding' }] : []),
  ];
  const tabs = (
    <Tabs
      variant="bar"
      label="Safety & public health"
      value={tab}
      onChange={setTab}
      items={items}
    />
  );
  if (tab === 'notif' || tab === 'incident')
    return (
      <OperationalRecords
        key={tab}
        title="Safety & public health"
        kinds={tab === 'notif' ? ['notifiable_case'] : ['incident']}
        before={tabs}
      />
    );
  if (tab === 'emerg')
    return (
      <OperationalRecords
        key={tab}
        title="Safety & public health"
        kinds={['emergency_box', 'emergency_check']}
        before={
          <>
            {tabs}
            <Emergencies />
          </>
        }
      />
    );
  return (
    <PageContainer>
      {tabs}
      {tab === 'obs' && (
        <Observations
          canManage={canManage}
          onCheck={(o) => setOpen({ kind: 'check', o })}
        />
      )}
      {tab === 'adr' && (
        <Reactions
          canManage={canManage}
          onNpra={(r) => setOpen({ kind: 'npra', r })}
        />
      )}
      {tab === 'safe' && canSafeguard && <SafeguardingList />}
      {open?.kind === 'check' && (
        <CheckDialog o={open.o} onClose={() => setOpen(null)} />
      )}
      {open?.kind === 'npra' && (
        <NpraDialog r={open.r} onClose={() => setOpen(null)} />
      )}
    </PageContainer>
  );
}

function Observations({
  canManage,
  onCheck,
}: {
  canManage: boolean;
  onCheck: (o: Observation) => void;
}) {
  const q = useSafety<Observation[]>('observations');
  return (
    <Card title="Observation after vaccination" flush>
      <DataTable
        caption="Observation after vaccination"
        rows={q.data}
        rowKey={(o) => o.recordId}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty={
          <EmptyState
            title="Nobody under observation"
            body="Recording a vaccination starts a 15-minute watch (30 minutes after a previous reaction)."
          />
        }
        columns={[
          { key: 'p', header: 'Patient', render: (o) => patient(o.patient) },
          {
            key: 'v',
            header: 'Vaccine',
            render: (o) => `${o.vaccine}${o.dose ? ` · dose ${o.dose}` : ''}`,
          },
          { key: 'g', header: 'Given', render: (o) => whenLabel(o.givenAt) },
          {
            key: 'u',
            header: 'Watch until',
            render: (o) =>
              `${whenLabel(o.watchUntil)} (${o.observeMinutes} min)`,
          },
          {
            key: 's',
            header: 'Status',
            render: (o) =>
              o.status === 'cleared' ? (
                <Badge tone="success">Cleared to go</Badge>
              ) : o.status === 'reaction' ? (
                <Badge tone="danger">Reaction</Badge>
              ) : o.status === 'watching' ? (
                <Badge tone="warning">Watching</Badge>
              ) : (
                <Badge tone="info">Check before leaving</Badge>
              ),
          },
          {
            key: 'x',
            header: '',
            render: (o) =>
              canManage &&
              (o.status === 'watching' || o.status === 'due_check') && (
                <Button size="sm" onClick={() => onCheck(o)}>
                  Check before leaving
                </Button>
              ),
          },
        ]}
      />
    </Card>
  );
}

function Reactions({
  canManage,
  onNpra,
}: {
  canManage: boolean;
  onNpra: (r: Reaction) => void;
}) {
  const q = useSafety<Reaction[]>('adverse-reactions');
  return (
    <Card title="Adverse drug reactions" flush>
      <DataTable
        caption="Adverse drug reactions"
        rows={q.data}
        rowKey={(r) => r.recordId}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty={<EmptyState title="No drug reactions recorded" />}
        columns={[
          { key: 'p', header: 'Patient', render: (r) => patient(r.patient) },
          { key: 'm', header: 'Medicine', render: (r) => r.medicine },
          {
            key: 'r',
            header: 'Reaction',
            render: (r) =>
              `${r.reaction}${r.severity ? ` · ${r.severity}` : ''}`,
          },
          {
            key: 'd',
            header: 'Recorded',
            render: (r) => (
              <>
                {whenLabel(r.recordedAt)}
                <span className={styles.sub}>{r.recordedBy}</span>
              </>
            ),
          },
          {
            key: 'n',
            header: 'NPRA',
            render: (r) =>
              r.npra ? (
                <Badge tone="success">
                  Sent{r.npra.reference ? ` ${r.npra.reference}` : ''}
                </Badge>
              ) : canManage ? (
                <Button size="sm" variant="primary" onClick={() => onNpra(r)}>
                  Mark sent
                </Button>
              ) : (
                <Badge tone="warning">Not reported</Badge>
              ),
          },
        ]}
      />
      <p className={styles.cardFoot}>
        Reported to NPRA outside the system; the allergy list was updated when
        the reaction was recorded.
      </p>
    </Card>
  );
}

function Emergencies() {
  const q = useSafety<Emergency[]>('emergencies');
  return (
    <Card title="Emergency events" flush>
      <DataTable
        caption="Emergency events"
        rows={q.data}
        rowKey={(e) => e.recordId}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty={
          <EmptyState title="No emergency steps recorded in the last 90 days" />
        }
        columns={[
          { key: 'w', header: 'When', render: (e) => whenLabel(e.recordedAt) },
          { key: 'p', header: 'Patient', render: (e) => patient(e.patient) },
          {
            key: 's',
            header: 'What happened',
            render: (e) =>
              `${e.step.replace(/_/g, ' ')}${e.note ? ` · ${e.note}` : ''}`,
          },
          { key: 'd', header: 'Doctor', render: (e) => e.recordedBy },
        ]}
      />
    </Card>
  );
}

function SafeguardingList() {
  const q = useSafety<Safeguarding[]>('safeguarding');
  return (
    <Card title="Safeguarding notes · restricted" flush>
      <DataTable
        caption="Safeguarding notes"
        rows={q.data}
        rowKey={(s) => s.recordId}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty={
          <EmptyState
            title="No safeguarding notes"
            body="Only doctors and the Super admin can see this list."
          />
        }
        columns={[
          { key: 'p', header: 'Patient', render: (s) => patient(s.patient) },
          {
            key: 'k',
            header: 'Concern',
            render: (s) =>
              s.kind === 'child_protection'
                ? `Child protection${s.summary.child ? ` · ${String(s.summary.child)}` : ''}`
                : 'Domestic violence',
          },
          {
            key: 'r',
            header: 'Recorded',
            render: (s) => `${whenLabel(s.recordedAt)} · ${s.recordedBy}`,
          },
          {
            key: 'i',
            header: 'Follow-up',
            render: (s) =>
              s.kind === 'child_protection' ? (
                s.summary.welfareInformedBy ? (
                  <Badge tone="success">
                    Welfare officer informed (
                    {String(s.summary.welfareInformedBy)})
                  </Badge>
                ) : (
                  <Badge tone="danger">Not yet informed</Badge>
                )
              ) : (
                String(s.summary.referral ?? '—').replace(/_/g, ' ')
              ),
          },
        ]}
      />
    </Card>
  );
}

function CheckDialog({ o, onClose }: { o: Observation; onClose: () => void }) {
  const a = useSafetyAction((v: { outcome: string; note?: string }) =>
    api.post(`/safety/observations/${o.recordId}/check`, v),
  );
  const [outcome, setOutcome] = useState('cleared');
  const [note, setNote] = useState('');
  return (
    <FormDialog
      title={`Check before leaving · ${o.patient.fullName}`}
      onClose={onClose}
      pending={a.isPending}
      disabled={outcome === 'reaction' && note.trim().length < 3}
      onSubmit={() =>
        a.mutateAsync({
          outcome,
          ...(note.trim() ? { note: note.trim() } : {}),
        })
      }
    >
      <Field label="Outcome">
        <Select
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          options={[
            { value: 'cleared', label: 'Cleared to go' },
            {
              value: 'reaction',
              label: 'Reaction (record it in the consultation too)',
            },
          ]}
        />
      </Field>
      <Field label="Note">
        <Input
          value={note}
          maxLength={300}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
    </FormDialog>
  );
}

function NpraDialog({ r, onClose }: { r: Reaction; onClose: () => void }) {
  const a = useSafetyAction((v: { reference?: string }) =>
    api.post(`/safety/adverse-reactions/${r.recordId}/npra`, v),
  );
  const [reference, setReference] = useState('');
  return (
    <FormDialog
      title={`Reported to NPRA · ${r.patient.fullName}`}
      onClose={onClose}
      pending={a.isPending}
      submitLabel="Mark sent"
      onSubmit={() =>
        a.mutateAsync(reference.trim() ? { reference: reference.trim() } : {})
      }
    >
      <p className={styles.notice}>
        Records that this reaction was reported to NPRA. Nothing is sent from
        here.
      </p>
      <Field label="NPRA reference (optional)">
        <Input
          value={reference}
          maxLength={60}
          onChange={(e) => setReference(e.target.value)}
        />
      </Field>
    </FormDialog>
  );
}
